import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import type { LucideIcon } from 'lucide-react';
import { Fragment, useState, type ReactElement } from 'react';
import { useBreakpoint } from '../../hooks/useMediaQuery';
import { Icon } from '../Icon/Icon';
import { Sheet } from '../Dialog/Dialog';
import s from './Menu.module.css';

export type MenuItem = {
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  tone?: 'danger';
  disabled?: boolean;
  /** Draw a divider above this item. */
  separatorBefore?: boolean;
};

export type MenuProps = {
  /** The trigger, usually an IconButton (e.g. "More options for Lego set"). */
  trigger: ReactElement;
  /** Sheet title on phones, e.g. "Lego Botanical Orchid". */
  label: string;
  items: MenuItem[];
};

/** Overflow/actions menu: a big-row bottom sheet on phones, a dropdown menu from md. */
export function Menu({ trigger, label, items }: MenuProps) {
  const desktop = useBreakpoint('md');
  const [open, setOpen] = useState(false);

  if (!desktop) {
    return (
      <Sheet open={open} onOpenChange={setOpen} trigger={trigger} title={label}>
        <ul className={s.sheetList}>
          {items.map((it) => (
            <Fragment key={it.key}>
              <li className={it.separatorBefore ? s.sheetDivided : undefined}>
                <button
                  type="button"
                  className={s.sheetItem}
                  data-tone={it.tone}
                  disabled={it.disabled}
                  onClick={() => {
                    setOpen(false);
                    it.onSelect();
                  }}
                >
                  {it.icon ? <Icon icon={it.icon} size="lg" /> : null}
                  {it.label}
                </button>
              </li>
            </Fragment>
          ))}
        </ul>
      </Sheet>
    );
  }

  return (
    <RadixMenu.Root modal={false}>
      <RadixMenu.Trigger asChild>{trigger}</RadixMenu.Trigger>
      <RadixMenu.Portal>
        <RadixMenu.Content
          className={s.menu}
          align="end"
          sideOffset={6}
          collisionPadding={12}
          aria-label={label}
        >
          {items.map((it) => (
            <Fragment key={it.key}>
              {it.separatorBefore ? <RadixMenu.Separator className={s.separator} /> : null}
              <RadixMenu.Item
                className={s.item}
                data-tone={it.tone}
                disabled={it.disabled}
                onSelect={it.onSelect}
              >
                {it.icon ? <Icon icon={it.icon} size="md" /> : null}
                {it.label}
              </RadixMenu.Item>
            </Fragment>
          ))}
        </RadixMenu.Content>
      </RadixMenu.Portal>
    </RadixMenu.Root>
  );
}
